import React, {useEffect, useRef, useState} from "react"
import {Link} from "react-router-dom"
import clsx from "clsx"
import ObjectID from "bson-objectid"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {IconName} from "@fortawesome/pro-regular-svg-icons"
import {Avatar, Button, CircularProgress, Progress, Tooltip, useToast} from "@chakra-ui/react"

import {ILabel, Vote, VoteData} from "@backend/types/modules"
import {CatIcon, Loading} from "@components/app"
import {IconNameExt} from "@utils/icons"
import {gamePlayApiCalls, GameStartType} from "../service"
import {History} from "./History"
import {PlayCard} from "../components/PlayCard"
import {FeedbackModal} from "./FeedbackModal"

// Types
type GameStatus = {
    status?: "gameLoading" | "textLoading" | "btnLoading" | "playing" | "endGame"
    currentQuestionPos: number
    currentLabelPos: number
    history: VoteData
}

type ReasonState = {
    showReasonModal: boolean
    voteKey?: string
}

type NextPosition = {
    questionPos: number
    labelPos: number
    isGameEnd: boolean
}

// Helper Functions
function calculateNextPosition(
    currentQuestionPos: number,
    currentLabelPos: number,
    totalQuestions: number,
    totalLabels: number
): NextPosition {
    let nextLabelPos = currentLabelPos + 1
    let nextQuestionPos = currentQuestionPos
    let isGameEnd = false

    if (nextLabelPos >= totalLabels) {
        nextLabelPos = 0
        nextQuestionPos += 1

        if (nextQuestionPos >= totalQuestions) {
            isGameEnd = true
        }
    }

    return {
        questionPos: nextQuestionPos,
        labelPos: nextLabelPos,
        isGameEnd
    }
}

function validateVoteDataConsistency(voteData: VoteData): boolean {
    if (!voteData || typeof voteData !== "object") return false

    for (const questionId in voteData) {
        const votes = voteData[questionId]
        if (!Array.isArray(votes)) return false

        for (const vote of votes) {
            if (!vote.questionId || !vote.labelId || !vote.vote || !vote.voteId) {
                return false
            }
        }
    }

    return true
}

export function Play({
                         gamePlay,
                         game: {
                             info,
                             modeOptions: {mode, fields, questionIds},
                             labelOptions: {setId},
                             voteOptions: {buttons}
                         }
                     }: GameStartType) {
    const toast = useToast()
    const voteSaveQueue = useRef<Vote[]>([])

    const [gameStatus, setGameStatus] = useState<GameStatus>({
        status: "playing",
        currentQuestionPos: 0,
        currentLabelPos: 0,
        history: gamePlay?.voteData || {}
    })

    const [reason, setReason] = useState<ReasonState>({
        showReasonModal: false
    })

    // Initialize game state
    useEffect(() => {
        if (Object.keys(gamePlay?.voteData || {}).length) {
            let currentQuestionPos = 0
            let currentLabelPos = 0

            // Validate vote data consistency
            if (!validateVoteDataConsistency(gamePlay.voteData)) {
                toast({
                    title: "Warning",
                    description: "Vote data consistency check failed",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                })
                return
            }

            // Calculate current position based on existing votes
            Object.keys(gamePlay.voteData).forEach(qId => {
                const votes = gamePlay.voteData[qId] || []
                if (votes.length && votes.length >= setId.labelIds.length) {
                    currentQuestionPos++
                } else {
                    currentLabelPos = votes.length
                }
            })

            setGameStatus(prevState => ({
                ...prevState,
                currentQuestionPos,
                currentLabelPos,
                status: "playing"
            }))
        }
    }, [gamePlay?.voteData, setId.labelIds.length])

    async function onLabelBtnClick(vote: string, reason?: string) {
        const {currentQuestionPos, currentLabelPos, history} = gameStatus
        const currentQuestion = questionIds[currentQuestionPos]
        const currentLabel = setId.labelIds[currentLabelPos] as ILabel

        // Validate current question and label
        if (!currentQuestion?._id || !currentLabel?._id) {
            toast({
                title: "Error",
                description: "Invalid question or label",
                status: "error",
                duration: 2000,
                isClosable: true,
            })
            return
        }

        setGameStatus(prevState => ({
            ...prevState,
            status: currentLabelPos + 1 === setId.labelIds.length ? "textLoading" : "btnLoading"
        }))

        try {
            const voteId = ObjectID().toHexString()

            // Create complete vote data
            const voteData: Vote = {
                questionId: currentQuestion,
                labelId: currentLabel,
                vote,
                voteId,
                reason,
                timestamp: new Date().toISOString(),
                data: {
                    currentQuestionPos,
                    currentQuestion,
                    currentLabel
                }
            }

            // Save to backend if gameplay exists
            if (gamePlay?._id) {
                const voteSaveData: Vote = {
                    questionId: currentQuestion._id,
                    labelId: currentLabel._id,
                    vote,
                    voteId,
                    reason,
                    timestamp: voteData.timestamp,
                    data: {
                        currentQuestionPos,
                        currentQuestion,
                        currentLabel
                    }
                }

                // Add to queue and process
                voteSaveQueue.current.push(voteSaveData)
                await gamePlayApiCalls.vote(gamePlay._id, voteSaveData)
                voteSaveQueue.current = voteSaveQueue.current.filter(v => v.voteId !== voteId)
            }

            // Update local history immutably
            const updatedHistory = {
                ...history,
                [currentQuestion._id]: [
                    ...(history[currentQuestion._id] || []),
                    voteData
                ]
            }

            // Calculate next position
            const nextPosition = calculateNextPosition(
                currentQuestionPos,
                currentLabelPos,
                questionIds.length,
                setId.labelIds.length
            )

            if (nextPosition.isGameEnd) {
                return endGame()
            }

            // Update game status
            setGameStatus({
                status: "playing",
                currentQuestionPos: nextPosition.questionPos,
                currentLabelPos: nextPosition.labelPos,
                history: updatedHistory
            })

        } catch (err) {
            toast({
                title: "Error",
                description: err.message,
                status: "error",
                duration: 2000,
                isClosable: true,
            })
            setGameStatus(prevState => ({
                ...prevState,
                status: "playing"
            }))
        }
    }

    async function onVoteChange({questionId, labelId, voteId, newVote}) {
        const {history} = gameStatus

        try {
            const updateVoteData = {
                questionId,
                newVote
            }

            if (gamePlay?._id) {
                await gamePlayApiCalls.voteUpdate(gamePlay._id, voteId, updateVoteData)
            }

            // Update local history immutably
            const questionVotes = history[questionId] || []
            const voteIndex = questionVotes.findIndex(x => x.voteId === voteId)

            if (voteIndex > -1) {
                const updatedQuestionVotes = [...questionVotes]
                updatedQuestionVotes[voteIndex] = {
                    ...updatedQuestionVotes[voteIndex],
                    vote: newVote
                }

                setGameStatus(prevState => ({
                    ...prevState,
                    history: {
                        ...history,
                        [questionId]: updatedQuestionVotes
                    }
                }))
            }
        } catch (err) {
            toast({
                title: "Error",
                description: "Failed to update vote",
                status: "error",
                duration: 2000,
                isClosable: true,
            })
        }
    }

    async function endGame() {
        if (gamePlay?._id) {
            try {
                await gamePlayApiCalls.endGame(gamePlay._id)
            } catch (err) {
                toast({
                    title: "Error",
                    description: "Failed to end game",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                })
            } finally {
                setGameStatus({
                    status: "endGame",
                    currentQuestionPos: 0,
                    currentLabelPos: 0,
                    history: {}
                })
            }
        }
    }

    function onReasonClose(voteKey: string, reason?: string) {
        setReason({
            showReasonModal: false,
        })
        onLabelBtnClick(voteKey, reason)
    }

    const currentQuestion = questionIds[gameStatus.currentQuestionPos]
    const currentLabel = (setId.labelIds as ILabel[])[gameStatus.currentLabelPos]

    const fieldsObj = fields.reduce((acc, item) => {
        acc[item.keyword] = item.title
        return acc
    }, {})

    if (gameStatus.status === "endGame") {
        return (
            <div>
                <PlayCard title="End Game">
                    <Button
                        colorScheme="blue"
                        className="underline"
                        variant="link"
                        as={Link}
                        to="/"
                    >
                        Go To Home Page
                    </Button>
                </PlayCard>
            </div>
        )
    }

    if (gameStatus.status === "gameLoading") {
        return <Loading/>
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center space-x-4">
                <Progress
                    bg="white"
                    value={(gameStatus.currentQuestionPos + 1) * (100 / questionIds.length)}
                    className="w-full rounded"
                />
                <span className="whitespace-nowrap">
                    {`${gameStatus.currentQuestionPos + 1} / ${questionIds.length}`}
                </span>
            </div>

            <div className={clsx(
                "grid grid-cols-1 gap-4",
                currentQuestion.texts.length > 1 && "md:grid-cols-2"
            )}>
                {currentQuestion.texts.map(({fieldId, text}) => (
                    <PlayCard
                        key={fieldId}
                        title={fieldsObj[fieldId]}
                        className="whitespace-break-spaces prose max-w-full col-span-1"
                        isLoading={gameStatus.status === "textLoading"}
                    >
                        <p>{text}</p>
                    </PlayCard>
                ))}
            </div>

            <PlayCard className="flex flex-col sticky bottom-4 border">
                <div className="flex items-center space-x-4 mb-6">
                    <CircularProgress value={gameStatus.currentLabelPos * (100 / setId.labelIds.length)}>
                        <Avatar
                            bg={setId.color}
                            style={{
                                position: "absolute",
                                top: 5,
                                left: 5,
                                width: 38,
                                height: 38
                            }}
                            icon={<CatIcon icon={setId.icon as IconNameExt} iconSize="lg"/>}
                        />
                    </CircularProgress>
                    <p className="text-base text-gray-900">
                        {currentLabel.questionStart || "Is this response"}{" "}
                        <Tooltip label={currentLabel.moreInfo}>
                            <strong className={currentLabel.moreInfo ? "underline" : undefined}>
                                {currentLabel.questionEnd}
                            </strong>
                        </Tooltip>
                    </p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {buttons.map((voteBtn) => (
                        <Button
                            key={voteBtn.key}
                            isLoading={["btnLoading", "textLoading"].includes(gameStatus.status)}
                            leftIcon={
                                <FontAwesomeIcon icon={["fal", voteBtn.icon as IconName]}/>
                            }
                            className="col-span-1"
                            bg={`${voteBtn.colorScheme}.500`}
                            color="white"
                            _hover={{opacity: "0.8"}}
                            onClick={() => {
                                if (voteBtn.askReason) {
                                    setReason({
                                        showReasonModal: true,
                                        voteKey: voteBtn.key
                                    })
                                } else {
                                    onLabelBtnClick(voteBtn.key)
                                }
                            }}
                            size="lg"
                        >
                            {voteBtn.text}
                        </Button>
                    ))}
                </div>
            </PlayCard>

            {Object.keys(gameStatus.history).length > 0 && (
                <PlayCard title="History" noTitleBorder>
                    <History
                        preview={!gamePlay?._id}
                        mode={mode}
                        history={gameStatus.history}
                        voteButtons={buttons}
                        onVoteChange={onVoteChange}
                    />
                </PlayCard>
            )}

            {reason.showReasonModal && (
                <FeedbackModal
                    message={currentQuestion.texts
                        .map(({fieldId, text}, i) =>
                            `${fieldsObj[fieldId] || (i + 1)} : ${text} \n\n`
                        )
                        .join("")}
                    voteKey={reason.voteKey}
                    onClose={onReasonClose}
                />
            )}
        </div>
    )
}